import { registerMockAPI } from '~/mocks/mockApis';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants/endpoint';

import { receiveKeys } from './keys';

import type {
  TypeAddGoodsModalGoods,
  TypeApiResponse,
  TypeGetRequestGoodsParams,
  TypeGetRequestGoodsResponse,
} from '~/types/receive.types';

const fetchRequestGoods = async (
  ownerCode: string,
  params: TypeGetRequestGoodsParams
): Promise<TypeGetRequestGoodsResponse> => {
  const { data } = await axiosInstance.get<TypeApiResponse<TypeGetRequestGoodsResponse>>(
    `${API_ENDPOINT.RECEIVE.GET_REQUEST_GOODS}/${ownerCode}/request/goods`,
    {
      params,
    }
  );
  return data.data;
};

type TypeUseGetRequestGoodsQueryParams = {
  ownerCode: string;
  params: TypeGetRequestGoodsParams;
  enabled?: boolean;
};

/**
 * [입고관리 > 입고요청관리] 입고상품추가 모달 > 상품 조회 Query Hook
 */
export const useGetRequestGoodsQuery = ({ ownerCode, params, enabled = true }: TypeUseGetRequestGoodsQueryParams) => {
  return useQuery({
    queryKey: receiveKeys.requestGoods(ownerCode, params),
    queryFn: () => fetchRequestGoods(ownerCode, params),
    enabled,
    staleTime: 0,
    gcTime: 1000 * 30,
    placeholderData: keepPreviousData,
  });
};

const buildGoods = (idx: number, overrides: Partial<TypeAddGoodsModalGoods> = {}): TypeAddGoodsModalGoods => ({
  goodsId: 17000 + idx,
  goodsCode: `EN000001${String(idx).padStart(4, '0')}`,
  nsid: `NSID-${String(idx).padStart(7, '0')}`,
  alternativeCode: `880000${String(idx).padStart(7, '0')}`,
  goodsName: `테스트 상품 ${idx}`,
  mainCategoryDesc: '가공식품',
  middleCategoryDesc: '간편식',
  thirdCategoryDesc: '시리얼/씨리얼바',
  kurlyKeepTypeDesc: '상온',
  shelfLifeTypeDesc: '소비기한',
  boxPerQty: 12,
  ...overrides,
});

// 다수 결과: '입고/입고요청/요청등록 — 정상 응답'에 사용된 EN0000010151을 첫 행에 두어
// 등록 모달이 상품을 1건 보유한 상태로 진입하면 비활성 행 분기도 함께 검증 가능.
const manyGoodsResponse: TypeApiResponse<TypeGetRequestGoodsResponse> = {
  data: {
    goodsList: [
      buildGoods(151, { goodsName: '닥터유 단백질 35g(3PL)', boxPerQty: 24 }),
      buildGoods(152, { goodsName: '닥터유 단백질 40g(3PL)' }),
      buildGoods(153, { goodsName: '닥터유 단백질바 50g', kurlyKeepTypeDesc: '냉장' }),
      buildGoods(154, { goodsName: '곰곰 견과류 믹스 200g', mainCategoryDesc: '신선식품' }),
      buildGoods(155, { goodsName: '곰곰 통밀 식빵', kurlyKeepTypeDesc: '냉장' }),
      buildGoods(156, { goodsName: '곰곰 그릭 요거트 450g', kurlyKeepTypeDesc: '냉장' }),
      buildGoods(157, { goodsName: '컬리 PB 시리얼 320g' }),
      buildGoods(158, { goodsName: '컬리 PB 단백질바 6P' }),
      buildGoods(159, { goodsName: '컬리 PB 즉석밥 12입', boxPerQty: 6 }),
      buildGoods(160, { goodsName: '컬리 PB 견과 30P', kurlyKeepTypeDesc: '냉동' }),
    ],
  },
};

const emptyGoodsResponse: TypeApiResponse<TypeGetRequestGoodsResponse> = {
  data: {
    goodsList: [],
  },
};

registerMockAPI<TypeApiResponse<TypeGetRequestGoodsResponse>>({
  method: 'get',
  endpoint: '/api-inbound/v1/3pl/receiving/:ownerCode/request/goods',
  label: '입고/입고요청/상품조회',
  scenarios: [
    { name: '정상 응답 — 10건', response: manyGoodsResponse },
    { name: '정상 응답 — 빈 결과', response: emptyGoodsResponse },
    {
      name: '잘못된 파라미터 (9001)',
      response: { error: { code: 9001, message: '상품 조회에 실패했습니다.\n다시 시도해 주세요.' } },
      status: 400,
    },
  ],
});
